import axios from "axios";
import { clearSession } from "../services/sessionStore";
import { getRunConfig } from "../config/runtimeConfig";
import {
  APPLICATION_NAME,
  APPLICATION_NAME_HEADER,
} from "./applicationHeader";

/**
 * Shared, module-level axios client.
 *
 * This is the single HTTP client for the app. It carries the cross-cutting concerns that used to
 * live inside the `useApi` hook's per-component interceptors:
 *   - `application-name` request header
 *   - `withCredentials` so the httpOnly `mtc_session` cookie rides along (auth)
 *   - 401 → clear the session and hard-redirect to "/"
 *   - route other errors to the global error dialog (unless the server set `suppressErrorDialog`)
 *
 * Being module-level (not a hook) matters for two things:
 *   1. `useApi()` returns this same instance, so its reference is stable across renders — even more
 *      stable than the old per-component `useRef`, which is friendly to effect/callback deps.
 *   2. It can be called from outside React — the TanStack Query fetchers (and, later, React Router
 *      loaders) reuse it, so they inherit auth + error handling with zero duplication.
 *
 * The one concern that can't live at module scope is the error dialog, which is React context
 * (`APIErrorProvider`). A bridge registers it at runtime via `setApiErrorHandler`; until then the
 * handler is a no-op, which matches "no dialog before the provider has mounted".
 *
 * Session state comes from `services/sessionStore`, NOT from `SessionService`. That is deliberate
 * and load-bearing: `SessionService` posts through this client, so importing it here would be a
 * cycle — the cycle that kept `/session/extend` and `/session/invalidate` on bare `axios.post`, and
 * therefore kept them out of the `application-name` header AUD-9 made mandatory. See the header of
 * `sessionStore.ts`.
 */

declare module "axios" {
  /**
   * Two per-request opt-outs from the response interceptor below. Both default to off; a request
   * that says nothing gets the app-wide behaviour, which is what almost every call site wants.
   *
   * They exist because the session-lifecycle calls now run on this client, and applying the global
   * handling to them is circular in the literal sense: the 401 handler's job is to end the session,
   * so running it on the response to "end my session" fights the caller.
   */
  export interface AxiosRequestConfig {
    /**
     * Don't raise the global error dialog for this request — the CALLER reports the failure.
     *
     * The request-side twin of the `suppressErrorDialog` flag the server can set on an error body
     * (see `ExternalGateResponse`, `MfaSetupGateResponse`). Same decision, made from the other end:
     * use the server's flag when a response is expected-and-quiet for everyone, and this one when a
     * particular screen has somewhere better to put the message. Setting it is a promise that the
     * call site reports failure itself — an unreported failure is worse than a generic dialog.
     */
    suppressErrorDialog?: boolean;

    /**
     * Don't run the global 401 handling (clear the session, hard-redirect to "/").
     *
     * Reserved for `SessionService.extend` / `.logout`, which ARE the session machinery: a 401 on
     * "extend" means the session is already gone, and `InactivityHandler` deliberately drives that
     * outcome itself — sign out, then navigate to `/login`. Left to the interceptor, the redirect
     * to "/" would race the caller's own navigation and the logout POST might never be sent.
     */
    skipUnauthorizedHandling?: boolean;

    /**
     * This request submits a credential the user just typed, so a 401 is the ANSWER to it — not a
     * verdict on the session.
     *
     * The global 401 branch reads every 401 as "your session is gone": clear it and hard-redirect
     * to "/". That is right for a request the session was supposed to authorize, and wrong for the
     * two that are themselves an authentication attempt, where the server is saying "those
     * credentials are incorrect" about a form field:
     *
     *  - `POST /auth/login` — bad credentials (NEW-31). The reload of "/" wiped the "Invalid
     *    username or password" message the screen had just raised, so the user got a blank login
     *    page and no reason.
     *  - `POST /auth/change-password` — wrong CURRENT password (NEW-38). Worse: that caller has a
     *    perfectly good session, so mistyping one field signed them out and bounced them to the
     *    root, never showing "Current password is incorrect."
     *
     * Deliberately NOT `skipUnauthorizedHandling`, which is reserved for the session machinery
     * itself (see above). These are the opposite case — a caller with nothing to do with session
     * lifecycle — and collapsing the two would make the reserved flag mean "sometimes" and lose the
     * one-line explanation of why each call site set it.
     *
     * Setting this is a promise that the call site renders the failure itself; both do.
     */
    credentialAttempt?: boolean;
  }
}

type ApiErrorHandler = (error: unknown) => void;

let apiErrorHandler: ApiErrorHandler = () => {};

/**
 * The `code` the API returns with a 403 when the account has not finished MFA setup — see
 * MfaSetupGateResponse.cs (AUD-15). Matching on the code, not the message text.
 */
export const MFA_SETUP_REQUIRED_CODE = "mfa_setup_required";

let mfaSetupRequiredHandler: () => void = () => {};

/**
 * Register what to do when the server refuses a request because MFA setup is unfinished.
 *
 * Normally this never fires: the setup gate now exists on both sides, and `RootLayout` renders the
 * setup screen instead of the app shell, so the calls that would be refused are never made. It
 * matters when the two disagree — a tab that has been open since before the phone verification was
 * reset, or a route loader that fires on a cold load with a deep URL before identity has arrived.
 * Re-reading `/identity` (which is outside the gate) republishes `mfaSetupRequired` and the app
 * lands on the setup screen instead of silently failing to render a page.
 */
export function setMfaSetupRequiredHandler(handler: () => void): void {
  mfaSetupRequiredHandler = handler;
}

/**
 * Register the function that surfaces API errors to the user (the context-bound `addError`).
 * Called once from a bridge effect inside `APIErrorProvider`.
 */
export function setApiErrorHandler(handler: ApiErrorHandler): void {
  apiErrorHandler = handler;
}

const { baseApiUrl } = getRunConfig();

const apiClient = axios.create({
  baseURL: baseApiUrl,
  withCredentials: true,
});

/**
 * The browser's IANA timezone, e.g. "America/Los_Angeles".
 *
 * Sent so the server can remember it for a user who has none, and show appointment times in THEIR
 * zone rather than the clinic's. Without it a patient in California was told their visit was at
 * "2:00 PM EDT" — correct, and useless. Resolved once: it cannot change mid-session, and a throw
 * here would break every request.
 */
const CLIENT_TIMEZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
})();

apiClient.interceptors.request.use(
  (config) => {
    // Required by the server on every mutating request since AUD-9 — see applicationHeader.ts.
    config.headers[APPLICATION_NAME_HEADER] = APPLICATION_NAME;
    // Advisory only: the server adopts it just for a user who has not set one, and never
    // overwrites a zone chosen in the profile.
    if (CLIENT_TIMEZONE) config.headers["X-Client-Timezone"] = CLIENT_TIMEZONE;
    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      !error.config?.skipUnauthorizedHandling &&
      !error.config?.credentialAttempt
    ) {
      // Session expired or invalid.
      clearSession();
      window.location.href = "/";
    }

    // Deliberately NOT a 401: the session is valid and has to stay valid, because the user needs it
    // to verify a phone. Nudge the app to re-read identity so it routes to the setup screen rather
    // than leaving a page that quietly failed to load.
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === MFA_SETUP_REQUIRED_CODE
    ) {
      mfaSetupRequiredHandler();
    }

    // Either end can ask us to keep quiet: the backend by setting suppressErrorDialog on the body
    // (e.g. the pending-approval 403), the call site by setting it on the request config when it
    // renders the failure itself.
    const suppressErrorDialog =
      error.response?.data?.suppressErrorDialog ||
      error.config?.suppressErrorDialog;
    if (!suppressErrorDialog) {
      apiErrorHandler(error);
    }

    return Promise.reject(error);
  },
);

export default apiClient;
